import React, { useEffect } from 'react';
import { ADMIN_PATH, PortalProvider, STAFF_PATH, STAFF_RESET_PATH, usePortal } from './contexts/PortalContext';
import { Login } from './pages/Login';
import { StaffLogin } from './pages/StaffLogin';
import { StaffResetPassword } from './pages/StaffResetPassword';
import { ClientApp } from './pages/ClientApp';
import { PortalApp } from './pages/PortalApp';
import { ViewToggle } from './components/ViewToggle';
import { PreviewBanner } from './components/PreviewBanner';
import { Toast } from './components/Toast';
import { CLIENT_ACCESS_TITLE, STAFF_TITLE, projectTitle, setPageTitle } from './utils/pageTitle';
function Portal() {
  const {
    path,
    navigate,
    staffAuthed,
    staffChecking,
    authed,
    previewAsClient,
    activeProject
  } = usePortal();
  const onAdmin = path.startsWith(ADMIN_PATH);
  const onReset = path.startsWith(STAFF_RESET_PATH);
  const onStaffLogin = path.startsWith(STAFF_PATH) && !onReset;

  /* Staff pages carry the Portal name; a client tab carries their own, taken
     from the stored client_name once the project has loaded. */
  useEffect(() => {
    const staffSide = onAdmin && !previewAsClient || onStaffLogin || onReset;
    if (staffSide) {
      setPageTitle(STAFF_TITLE);
      return;
    }
    if (!authed && !previewAsClient) {
      setPageTitle(CLIENT_ACCESS_TITLE);
      return;
    }
    setPageTitle(projectTitle(activeProject.client));
  }, [onAdmin, onStaffLogin, onReset, previewAsClient, authed, activeProject.client]);

  // /admin is protected: without a verified Supabase staff session you are sent
  // to /staff, and knowing the URL is never enough. Signed in, /staff hands you
  // on to /admin. Both wait for the persisted session to be checked first.
  useEffect(() => {
    if (staffChecking) return;
    if (onAdmin && !staffAuthed) navigate(STAFF_PATH);
    if (onStaffLogin && staffAuthed) navigate(ADMIN_PATH);
  }, [onAdmin, onStaffLogin, staffAuthed, staffChecking, navigate]);

  /* A recovery link must reach its own form, never the sign-in page — and the
     recovery session must not carry anyone into the dashboard. */
  if (onReset) {
    return <>
        <StaffResetPassword data-id="element-0" data-mp="3ucje3y95kjvu" />
        <Toast data-id="element-1" data-mp="d4o0nv3yoa99" />
      </>;
  }
  if (onAdmin) {
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-2" data-mp="d2l7j27ii10b" />;
    if (!staffAuthed) {
      return <>
          <StaffLogin data-id="element-3" data-mp="83qhbf77ta66" />
          <Toast data-id="element-4" data-mp="3gl4pnbwh7qhe" />
        </>;
    }
    if (previewAsClient) {
      return <>
          <PreviewBanner data-id="element-5" data-mp="3bmjo995hn0rd" />
          <ClientApp data-id="element-6" data-mp="3bncorsdusu12" />
          <Toast data-id="element-7" data-mp="3bo4mb8r0h1tz" />
        </>;
    }
    return <>
        <PortalApp data-id="element-8" data-mp="3qbfur1rwc5pv" />
        <ViewToggle data-id="element-9" data-mp="3qbzd2jlgkw4f" />
        <Toast data-id="element-10" data-mp="3qdaienujybxn" />
      </>;
  }
  if (onStaffLogin) {
    return <>
        <StaffLogin data-id="element-11" data-mp="3liwlgh08rwqv" />
        <ViewToggle data-id="element-12" data-mp="3liehey7c8e8e" />
        <Toast data-id="element-13" data-mp="2xun35p28lr4h" />
      </>;
  }
  if (!authed) {
    return <>
        <Login data-id="element-14" data-mp="2xssj0cysi31g" />
        <ViewToggle data-id="element-15" data-mp="2svtnbpzh9o15" />
      </>;
  }
  return <>
      <ClientApp data-id="element-16" data-mp="2svaf50zmqe0b" />
      <ViewToggle data-id="element-17" data-mp="2stplrep1x141" />
      <Toast data-id="element-18" data-mp="18drfvlydcx92" />
    </>;
}
export function App() {
  return <PortalProvider data-id="element-19" data-mp="18cgettwb63ln">
      <Portal data-id="element-20" data-mp="18by8g4fxm900" />
    </PortalProvider>;
}